import React, { ChangeEvent } from 'react';

import { fakeChangeEvent } from 'utils/dom';

import { PhoneInput, Country } from './index';

type Configuration = { value: string; country: Country };
type Value = string;

function isConfiguration(
  value: undefined | Value | Configuration,
): value is Configuration {
  if (typeof value === 'undefined') return false;
  if (typeof value === 'string') return false;
  return true;
}

function SetValue<T>({
  value,
  onChange,
}: {
  value: T;
  onChange: (value: T) => void;
}) {
  React.useEffect(() => onChange(value), [value, onChange]);
  return null;
}

function ChangeValue({
  value,
  onChange,
  name,
}: {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  name: string;
}) {
  return (
    <input
      type="hidden"
      onChange={onChange}
      name={name}
      ref={el => {
        if (el) {
          fakeChangeEvent(el, value);
          // For some reason, the fake change event does not reach react
          // when the target value is an empty string. Therefore in those cases
          // we need to call the callback ourselves with something that looks
          // like a react event
          if (value === '') {
            onChange({ target: el, persist: () => undefined } as any);
          }
        }
      }}
    />
  );
}

/**
 * Like PhoneInput, but value and defaultValue will also accept
 * a {value, country} pair instead of a regular string.
 *
 * Upon receipt of such an object, change events are immediately emitted
 * for both the value and country, and the input is rendered as if normal strings had been provided
 */
export function PhoneOrConfigurationInput(
  props: Omit<Parameters<typeof PhoneInput>[0], 'value' | 'defaultValue'> & {
    value?: Value | Configuration;
    defaultValue?: Value | Configuration;
  },
) {
  const { value, defaultValue, ...rest } = props;
  const {
    onChange = () => undefined,
    onChangeCountry = () => undefined,
    name = '',
  } = props;

  // IF value given as conf, use that as the value and emit events
  if (isConfiguration(value)) {
    const { value: v, country: c } = value;
    return (
      <>
        <PhoneInput {...rest} value={v} country={c} />
        <ChangeValue value={v} onChange={onChange} name={name} />
        <SetValue value={c} onChange={onChangeCountry} />
      </>
    );
  }

  // If defaultValue given as conf, use that as the defaultvalue and emit events
  // but be careful not to let the intiial values override existing full values
  if (isConfiguration(defaultValue)) {
    if (value === undefined && props.country === undefined) {
      // No full values, use and update both
      const { value: v, country: c } = defaultValue;
      return (
        <>
          <PhoneInput {...rest} defaultValue={v} defaultCountry={c} />
          <ChangeValue value={v} onChange={onChange} name={name} />
          <SetValue value={c} onChange={onChangeCountry} />
        </>
      );
    }
    if (value === undefined) {
      // default value, but country is controlled.
      // ignore default country, set number to ""
      const { value: v, country: c } = defaultValue;
      return (
        <>
          <PhoneInput {...rest} defaultValue="" defaultCountry={c} />
          <ChangeValue value="" onChange={onChange} name={name} />
        </>
      );
    }
    if (props.country === undefined) {
      // Default value, but number is controlled
      // Ignore default value, reset country to unknown
      const { value: v, country: c } = defaultValue;
      return (
        <>
          <PhoneInput {...rest} defaultValue={v} defaultCountry="unknown" />
          <SetValue value="unknown" onChange={onChangeCountry} />
        </>
      );
    }
    // Value and country both defined, ignore defaultValue entirely
    return <PhoneInput {...rest} />;
  }

  // Nothing given as conf, can run phoneinput normally
  return <PhoneInput value={value} defaultValue={defaultValue} {...rest} />;
}
